import { useContext } from 'react';
import { LanguageContext } from '../contexts/LanguageContext';
import { ComposedChart, Bar, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Custom shape for Scatter
const CustomCircleShape = (props) => {
  const { cx, cy } = props;
  return (
    <circle cx={cx} cy={cy} r={9} stroke="var(--ppe-green)" strokeWidth={1} fill="var(--ppe-orange)"/>
  );
};


const CustomTriangle = (props) => {
  const { cx, cy } = props;
  const size = 18; // height of triangle
  const halfBase = 9; // half width of base

  // Points: top (cx, cy), bottom left, bottom right
  const points = [
    `${cx},${cy}`, // top
    `${cx - halfBase},${cy + size}`, // bottom left
    `${cx + halfBase},${cy + size}`  // bottom right
  ].join(' ');

  return (
    <polygon
      points={points}
      stroke="var(--ppe-green)"
      strokeWidth={0.5}
      fill="var(--ppe-orange)"
    />
  );
};

// New index -> old index mapping for version 3 order.
const VERSION3_REORDER_ORDER = [0, 5, 8, 1, 4, 7, 6, 3, 2];

const reorderByOrder = (items, order) => {
  if (!Array.isArray(items)) {
    return [];
  }

  if (!Array.isArray(order) || order.length !== items.length) {
    console.warn('BarGraph: invalid reorder order length, keeping original order.', {
      itemsLength: items.length,
      orderLength: Array.isArray(order) ? order.length : null,
    });
    return items;
  }

  const usedOldIndexes = new Set();
  const reordered = [];

  for (let newIndex = 0; newIndex < order.length; newIndex += 1) {
    const oldIndex = order[newIndex];
    const hasValidOldIndex = Number.isInteger(oldIndex) && oldIndex >= 0 && oldIndex < items.length;

    if (!hasValidOldIndex) {
      console.warn('BarGraph: invalid old index in reorder order, using same position fallback.', { newIndex, oldIndex });
      reordered.push(items[newIndex]);
      continue;
    }

    if (usedOldIndexes.has(oldIndex)) {
      console.warn('BarGraph: duplicate old index in reorder order, using same position fallback.', { newIndex, oldIndex });
      reordered.push(items[newIndex]);
      continue;
    }

    usedOldIndexes.add(oldIndex);
    reordered.push(items[oldIndex]);
  }

  return reordered;
};

export default function BarGraph({ data, cp = false }) {
    const isCognitiveRow = (row) => {
      const values = [row?.fr, row?.en, row?.de, row?.it, row?.es, row?.label]
        .map((v) => String(v || '').toLowerCase().trim());

      return values.some((v) => (
        v === 'style cognitif' ||
        v === 'cognitive style' ||
        v === 'capacite de raisonnement' ||
        v === 'capacité de raisonnement' ||
        v === 'capacity for reasoning' ||
        v === 'logical reasoning' ||
        v === 'logisches denken'
      ));
    };

  const langInf = { "fr": "tiers inférieur", "en": "lower third", "de": "unteres Drittel", "it": "terzo inferiore", "es": "tercio inferior" }
  const langMid = { "fr": "tiers moyen", "en": "middle third", "de": "mittleres Drittel", "it": "terzo medio", "es": "tercio medio" }
  const langUp =  { "fr": "tiers supérieur", "en": "upper third", "de": "oberes Drittel", "it": "terzo superiore", "es": "tercio superior" }
  const langCandidate = { "fr": "candidat(e)", "en": "candidate", "de": "Kandidat(in)", "it": "candidato/a", "es": "candidato/a" }
  const { locale } = useContext(LanguageContext);

  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {payload.map((entry, index) => (
          <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
            {entry.value === langCandidate[locale] ? (
              <svg width="20" height="20" viewBox="0 0 20 20">
                <polygon points="10,2 18,18 2,18" fill={entry.color} />
              </svg>
            ) : (
              <div style={{ width: 20, height: 10, backgroundColor: entry.color, marginRight: 5 }}></div>
            )}
            <span style={{ marginLeft: 5 }}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  let cH = "100%";
  let cW ="90%";
  let ch=600;
  let wh=200;
  let layout = "vertical";
  let bs = 10; // bar width
  let bm = 22; // bar width for middle third Candle
  let adjustLabelsWidth = 250; // width of the labels on the Y axis
  let adjustLabelsFontSize = 14; // font size of the labels on the Y axis
  let altFill = "var(--ppe-green-10)";
  let cndlBody = false;
  const hasCognitiveStyle = Array.isArray(data) && data.some(item => item?.fr === "Style cognitif");
  if (hasCognitiveStyle && !cp) {
    cndlBody = true;
    altFill = "var(--ppe-green-80)";
    bs = 3; // bar - candle wick - for personality traits
  }

  // Detect FAITS VS OPINIONS single-value data and apply FO-specific sizing
  const isFaitsOpinionsData = Array.isArray(data) && data.length === 1 && (() => {
    const item = data[0] || {};
    const txt = ((item.fr || '') + ' ' + (item.en || '') + ' ' + (item.label || '')).toLowerCase();
    return txt.includes('faits') || txt.includes('faits vs') || txt.includes('faits_opinions') || txt.includes('faits opinions') || txt.includes('faits_vs');
  })();

  if (isFaitsOpinionsData && !cp) {
    // FO-specific visual tweaks (only for the FAITS VS OPINIONS chart)
    bs = 20;        // bar thickness for FO
    cH = 200;       // responsive container height
    ch = 200;       // inner ComposedChart height
    wh = 800;       // inner ComposedChart width (ResponsiveContainer will scale)
    cW = "100%";   // make container full-width
    adjustLabelsWidth = 0;  // hide Y-axis label for FO chart
  }


  const CandleBody = (props) => {
    const { x, y, width } = props;
    return <rect x={x} y={y-(bm/2)+1.5} width={width} height={bm} fill={"var(--ppe-green-40)"} rx={2} />;
  };

 const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Compute cumulative values for lower, middle, upper, user
    const entry = payload[0]?.payload || {};
    return (
      <div className="custom-tooltip" style={{ background: "#fff", border: "1px solid #ccc", padding: 10 }}>
        <p className="label"><b>{label}</b></p>
        <p style={{ color: "var(--ppe-green-80)", margin: 0 }}>
          {langInf[locale]}: <b>{entry.rawLowerthird}</b>
        </p>
        <p style={{ color: "var(--ppe-green-40)", margin: 0 }}>
          {langMid[locale]}: <b>{entry.rawMiddlethird}</b>
        </p>
        <p style={{ color: "var(--ppe-green-10)", margin: 0 }}>
          {langUp[locale]}: <b>{entry.rawUpperthird}</b>
        </p>
        <p style={{ color: "var(--ppe-orange)", margin: 0 }}>
          {langCandidate[locale]}: <b>{entry.rawUser}</b>
        </p>
      </div>
    );
  }
  return null;
};

  if(cp){
    bs = 40; // bar width for Cognitive style
    layout = "horizontal";
    cW="100%";
    cH="100%";
    data = data.filter((row) => isCognitiveRow(row))
      .map(({ de, en, fr, it, es, fullMark, lowerThird, middleThird, upperThird, user }) => {
        let lowerthird = Number(lowerThird);
        let middlethird = Number(middleThird);
        let upperthird = Number(upperThird);
        let fullmark = Number(fullMark);
        let rawLowerthird = lowerthird;
        let rawMiddlethird = lowerthird + middlethird;
        let rawUpperthird = lowerthird + middlethird + upperthird;
        let rawUser = Number(user);
        user = Number(user);

        return {
          de,
          en,
          fr,
          it,
          es,
          fullmark,
          lowerthird: lowerthird.toFixed(2),
          middlethird: middlethird.toFixed(2),
          upperthird: upperthird.toFixed(2),
          user: user.toFixed(2),
          rawLowerthird: rawLowerthird.toFixed(2),
          rawMiddlethird: rawMiddlethird.toFixed(2),
          rawUpperthird: rawUpperthird.toFixed(2),
          rawUser: rawUser.toFixed(2)
        };
      });
    console.log("data CP: ", data);
}
else {
  data = data
    .filter(({ fr }) => fr !== "Style cognitif")
    .map(({ de, en, fr, it, es, fullMark, lowerThird, middleThird, upperThird, user }) => {
      let lowerthird = Number(lowerThird);
      let middlethird = Number(middleThird);
      let upperthird = Number(upperThird);
      let fullmark = Number(fullMark);
      let rawLowerthird = Number(lowerThird);
      let rawMiddlethird = Number(lowerThird) + Number(middleThird);
      let rawUpperthird = Number(lowerThird) + Number(middleThird) + Number(upperThird);
      let rawUser = Number(user);
      user = Number(user);

      if (cndlBody) {
        adjustLabelsWidth = 70;
        adjustLabelsFontSize = 0;
        cH = 890;
        cW = 300;
        const totalValue = lowerthird + middlethird + upperthird;
        if (totalValue > 0) {
          const scale = fullmark / totalValue;
          lowerthird = lowerthird * scale;
          middlethird = middlethird * scale;
          upperthird = upperthird * scale;
          user = user * scale;
        }
      }

      return {
        de,
        en,
        fr,
        it,
        es,
        fullmark,
        lowerthird: lowerthird.toFixed(2),
        middlethird: middlethird.toFixed(2),
        upperthird: upperthird.toFixed(2),
        user: user.toFixed(2),
        rawLowerthird: rawLowerthird.toFixed(2),
        rawMiddlethird: rawMiddlethird.toFixed(2),
        rawUpperthird: rawUpperthird.toFixed(2),
        rawUser: rawUser.toFixed(2)
      };
    });

  if (Array.isArray(data) && data.length === 9) {
    data = reorderByOrder(data, VERSION3_REORDER_ORDER);
  }
}
  // Ensure the numeric axis covers the full mark (e.g. 0..18)
  const maxFull = (Array.isArray(data) && data.length) ? Number(data[0].fullmark || data[0].fullMark || 18) : 18;
  return cndlBody ? (
    <ComposedChart layout={layout} width={300} height={891} data={data} margin={{ top: 20, right: 37, bottom: 20, left: 0 }}>
      <CartesianGrid stroke='#f5f5f5' />
      {layout === "vertical" ? (
        <>
          <XAxis type="number" domain={[0, maxFull]} tickCount={Math.max(2, maxFull + 1)} axisLine={cndlBody ? false : true} tick={cndlBody ? false : true} />
          <YAxis dataKey={locale} type="category" axisLine={cndlBody ? false : true} tick={(cndlBody || isFaitsOpinionsData) ? false : { fontSize: adjustLabelsFontSize }} width={adjustLabelsWidth} />
        </>
      ) : (
        <>
          <XAxis dataKey={locale} type="category" tick={{ fontSize: 16 }} />
          <YAxis type="number" /> 
        </>
      )}
      <Tooltip content={<CustomTooltip />} />
      <Legend content={cndlBody ? renderLegend : undefined} width={cndlBody ? 365 : 600} left={cndlBody ? -50 : 20} payload={
          cndlBody
            ? [
                {
                  value: langMid[locale], // e.g. "middle third"
                  type: "rect",
                  id: "middlethird",
                  color: "var(--ppe-green-40)"
                },
                {
                  value: langCandidate[locale], // e.g. "candidate"
                  type: "triangle",
                  id: "user",
                  color: "var(--ppe-orange)"
                }
              ]
            : undefined
        }/>
      <Bar stackId="a" name={langInf[locale]} dataKey='lowerthird' barSize={bs} fill='var(--ppe-green-80)' />
      <Bar stackId="a" name={langMid[locale]} dataKey='middlethird' barSize={bs} fill='var(--ppe-green-40)'  shape={cndlBody ? <CandleBody /> : undefined}  />
      <Bar stackId="a" name={langUp[locale]} dataKey='upperthird' barSize={bs} fill={altFill} />
      { layout === "vertical" && !cndlBody && <Scatter
        name={langCandidate[locale]}
        dataKey="user"
        fill="var(--ppe-orange)"
        shape={<CustomCircleShape />}
      />
      }
      {
        cndlBody && console.log("doubt de soi", data[4])
      }
      { cndlBody && <Scatter
        name={langCandidate[locale]}
        dataKey="user"
        fill="var(--ppe-orange)"
        shape={<CustomTriangle />}
      />
      }
      { layout !== "vertical" && !cndlBody && <Scatter
        name={langCandidate[locale]}
        dataKey="user"
        fill="var(--ppe-orange)"
        shape={<CustomCircleShape />}
      />
      }
    </ComposedChart>
  ) : isFaitsOpinionsData ? (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <ResponsiveContainer width="90%" height={cH}>
        <ComposedChart layout={layout} width={wh} height={ch} data={data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
        <CartesianGrid stroke='#f5f5f5' />
        {layout === "vertical" ? (
          <>
            <XAxis type="number" domain={[0, maxFull]} tickCount={Math.max(2, maxFull + 1)} axisLine={cndlBody ? false : true} tick={cndlBody ? false : true} />
            <YAxis dataKey={locale} type="category" axisLine={cndlBody ? false : true} tick={(cndlBody || isFaitsOpinionsData) ? false : { fontSize: adjustLabelsFontSize }} width={adjustLabelsWidth} />
          </>
        ) : (
          <>
            <XAxis dataKey={locale} type="category" tick={{ fontSize: 16 }} />
            <YAxis type="number" />
          </>
        )}
        <Tooltip content={<CustomTooltip />} />
        <Legend align="center" iconType="square" payload={
            [
              { value: langInf[locale], type: "square", id: "lowerthird", color: "var(--ppe-green-80)" },
              { value: langMid[locale], type: "square", id: "middlethird", color: "var(--ppe-green-40)" },
              { value: langUp[locale], type: "square", id: "upperthird", color: "var(--ppe-green-10)" },
              { value: langCandidate[locale], type: "circle", id: "user", color: "var(--ppe-orange)" },
            ]
          }/>
        <Bar stackId="a" name={langInf[locale]} dataKey='lowerthird' barSize={bs} fill='var(--ppe-green-80)' />
        <Bar stackId="a" name={langMid[locale]} dataKey='middlethird' barSize={bs} fill='var(--ppe-green-40)' />
        <Bar stackId="a" name={langUp[locale]} dataKey='upperthird' barSize={bs} fill={altFill} />
        { layout === "vertical" && <Scatter
          name={langCandidate[locale]}
          dataKey="user"
          fill="var(--ppe-orange)"
          shape={<CustomCircleShape />}
        />
        }
      </ComposedChart>
    </ResponsiveContainer>
    </div>
  ) : (
    <ResponsiveContainer width={cW} height={cH}>
      <ComposedChart layout={layout} width={wh} height={ch} data={data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
        <CartesianGrid stroke='#f5f5f5' />
        {layout === "vertical" ? (
          <>
            <XAxis type="number" domain={[0, maxFull]} tickCount={Math.max(2, maxFull + 1)} axisLine={cndlBody ? false : true} tick={cndlBody ? false : true} />
            <YAxis dataKey={locale} type="category" axisLine={cndlBody ? false : true} tick={(cndlBody || isFaitsOpinionsData) ? false : { fontSize: adjustLabelsFontSize }} width={adjustLabelsWidth} />
          </>
        ) : (
          <>
            <XAxis dataKey={locale} type="category" tick={{ fontSize: 16 }} />
            <YAxis type="number" />
          </>
        )}
        <Tooltip content={<CustomTooltip />} />
        <Legend content={cndlBody ? renderLegend : undefined} width={cndlBody ? 365 : 600} left={cndlBody ? -50 : 20} payload={
            cndlBody
              ? [
                  {
                    value: langMid[locale], // e.g. "middle third"
                    type: "rect",
                    id: "middlethird",
                    color: "var(--ppe-green-40)"
                  },
                  {
                    value: langCandidate[locale], // e.g. "candidate"
                    type: "triangle",
                    id: "user",
                    color: "var(--ppe-orange)"
                  }
                ]
              : undefined
          }/>
        <Bar stackId="a" name={langInf[locale]} dataKey='lowerthird' barSize={bs} fill='var(--ppe-green-80)' />
        <Bar stackId="a" name={langMid[locale]} dataKey='middlethird' barSize={bs} fill='var(--ppe-green-40)'  shape={cndlBody ? <CandleBody /> : undefined}  />
        <Bar stackId="a" name={langUp[locale]} dataKey='upperthird' barSize={bs} fill={altFill} />
        { layout === "vertical" && !cndlBody && <Scatter
          name={langCandidate[locale]}
          dataKey="user"
          fill="var(--ppe-orange)"
          shape={<CustomCircleShape />}
        />
        }
        { cndlBody && <Scatter
          name={langCandidate[locale]}
          dataKey="user"
          fill="var(--ppe-orange)"
          shape={<CustomTriangle />}
        />
        }
        { layout !== "vertical" && !cndlBody && console.log("non vertical values", data) }
        { layout !== "vertical" && !cndlBody && <Scatter
          name={langCandidate[locale]}
          dataKey="user"
          fill="var(--ppe-orange)"
          shape={<CustomCircleShape />}
        />
        }
      </ComposedChart>
    </ResponsiveContainer>
  );
}